import React, { useEffect, useState } from "react";
import { Row, Col, Card } from "react-bootstrap";
import MetricCard from "./MetricCard";
import TrendChart from "./TrendChart";

const IndividualMetrics = ({ period, reduxData, loading, getPeriodLabel, employeeId, employeeName }) => {
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    if (reduxData && Object.keys(reduxData).length > 0) {
      setMetrics(reduxData); // ← use API data
    }
  }, [reduxData]);

  if (!metrics) return <div>Loading...</div>;

  return (
    <div className="individual-metrics">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <i className="fas fa-user me-2 icon-orange"></i>
          {employeeName ? `${employeeName}'s Performance` : "My Performance"} - {getPeriodLabel(period)}
        </h5>
        <div className="text-muted small">
          Last updated: {new Date().toLocaleString()}
        </div>
      </div>

      {/* Alert for Oldest Open Ticket */}
      {metrics.oldest_open_ticket_age_hours && metrics.oldest_open_ticket_age_hours > 72 && (
        <Row className="mb-3">
          <Col>
            <Card className="border-warning bg-warning bg-opacity-10">
              <Card.Body className="py-2">
                <div className="d-flex align-items-center">
                  <i className="fas fa-exclamation-triangle text-warning me-2" style={{ fontSize: "1.5rem" }}></i>
                  <div className="flex-grow-1">
                    <strong className="text-warning">Attention:</strong> Your oldest open ticket is{" "}
                    <strong>{Math.floor(metrics.oldest_open_ticket_age_hours / 24)} days old</strong>
                    {" "}({metrics.oldest_open_ticket_age_hours.toFixed(1)} hours)
                  </div>
                  <span className="badge bg-warning text-dark">Action Required</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Primary Metrics - Ticket Counts */}
      <Row className="mb-4">
        <Col lg={3} md={6} sm={6} className="mb-3">
          <MetricCard
            title="Total Assigned"
            value={metrics.total_assigned || 0}
            icon="fa-inbox"
            color="#0d6efd"
          />
        </Col>
        <Col lg={3} md={6} sm={6} className="mb-3">
          <MetricCard
            title="Total Resolved"
            value={metrics.total_resolved || 0}
            icon="fa-check-circle"
            color="#28a745"
          />
        </Col>
        <Col lg={3} md={6} sm={6} className="mb-3">
          <MetricCard
            title="Active Tickets"
            value={metrics.active_tickets || 0}
            icon="fa-tasks"
            color="#ffc107"
          />
        </Col>
        <Col lg={3} md={6} sm={6} className="mb-3">
          <MetricCard
            title="Auto Closed"
            value={metrics.auto_closed_tickets || 0}
            icon="fa-robot"
            color="#17a2b8"
            subtitle="System resolved"
          />
        </Col>
      </Row>

      {/* New KPI Cards */}
      <Row className="mb-4">
        {/* Ticket Count by Status */}
        <Col md={4} className="mb-3">
          <Card className="shadow-sm h-100">
            <Card.Body>
              <h6 className="mb-3">
                <i className="fas fa-list-check me-2 text-primary"></i>
                Ticket count by status
              </h6>
              <div className="d-flex flex-column gap-2">
                {metrics.statusBreakdown && metrics.statusBreakdown.map((status, idx) => (
                  <div key={idx} className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                    <div className="d-flex align-items-center">
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: status.color || "#6c757d",
                          marginRight: "10px"
                        }}
                      ></div>
                      <span className="small">{status.status}</span>
                    </div>
                    <strong style={{ color: status.color || "#6c757d" }}>{status.count}</strong>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Top 5 Categories */}
        <Col md={4} className="mb-3">
          <Card className="shadow-sm h-100">
            <Card.Body>
              <h6 className="mb-3">
                <i className="fas fa-chart-pie me-2 text-success"></i>
                Top 5 Categories
              </h6>
              <div className="d-flex flex-column gap-2">
                {metrics.topCategories && metrics.topCategories.map((category, idx) => (
                  <div key={idx} className="d-flex justify-content-between align-items-start p-2 bg-light rounded">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="small text-truncate" title={category.name}>
                        {category.name}
                      </div>
                      <div className="text-muted" style={{ fontSize: "10px" }}>
                        {category.subcategory}
                      </div>
                    </div>
                    <strong className="ms-2" style={{ color: category.color || "#6c757d", whiteSpace: "nowrap" }}>
                      {category.count}
                    </strong>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Ticket Assignment */}
        <Col md={4} className="mb-3">
          <Card className="shadow-sm h-100">
            <Card.Body>
              <h6 className="mb-3">
                <i className="fas fa-user-check me-2 text-warning"></i>
                Ticket Assignment
              </h6>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                  <div>
                    <div className="text-muted small">
                      <i className="fas fa-random me-2 text-primary"></i>
                      Routing Rules
                    </div>
                    <small className="text-muted" style={{ fontSize: "10px" }}>
                      Auto-assigned
                    </small>
                  </div>
                  <strong style={{ fontSize: "1.5rem", color: "#0d6efd" }}>
                    {metrics.assignment?.routingRules || 0}
                  </strong>
                </div>
                <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                  <div>
                    <div className="text-muted small">
                      <i className="fas fa-user me-2 text-success"></i>
                      Self
                    </div>
                    <small className="text-muted" style={{ fontSize: "10px" }}>
                      Self-assigned
                    </small>
                  </div>
                  <strong style={{ fontSize: "1.5rem", color: "#28a745" }}>
                    {metrics.assignment?.self || 0}
                  </strong>
                </div>
                <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                  <div>
                    <div className="text-muted small">
                      <i className="fas fa-users me-2 text-warning"></i>
                      Other
                    </div>
                    <small className="text-muted" style={{ fontSize: "10px" }}>
                      Manually assigned
                    </small>
                  </div>
                  <strong style={{ fontSize: "1.5rem", color: "#ffc107" }}>
                    {metrics.assignment?.other || 0}
                  </strong>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Trend Charts - Hidden for daily view */}
      {metrics.trends && period !== "daily" && (
        <Row>
          <Col md={4} className="mb-3">
            <Card className="shadow-sm h-100">
              <Card.Body>
                <h6 className="mb-3">
                  <i className="fas fa-chart-area me-2 text-primary"></i>
                  Assigned Tickets Trend
                </h6>
                <TrendChart
                  data={metrics.trends.assignedTrend}
                  label="Assigned"
                  color="#0d6efd"
                  period={period}
                />
              </Card.Body>
            </Card>
          </Col>
          <Col md={4} className="mb-3">
            <Card className="shadow-sm h-100">
              <Card.Body>
                <h6 className="mb-3">
                  <i className="fas fa-chart-area me-2 text-success"></i>
                  Resolved Tickets Trend
                </h6>
                <TrendChart
                  data={metrics.trends.resolvedTrend}
                  label="Resolved"
                  color="#28a745"
                  period={period}
                />
              </Card.Body>
            </Card>
          </Col>
          <Col md={4} className="mb-3">
            <Card className="shadow-sm h-100">
              <Card.Body>
                <h6 className="mb-3">
                  <i className="fas fa-chart-area me-2 text-info"></i>
                  Response Time Trend
                </h6>
                <TrendChart
                  data={metrics.trends.responseTimeTrend}
                  label="Response Time (hours)"
                  color="#17a2b8"
                  period={period}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default IndividualMetrics;
